<template>
    <v-container fluid class="white px-0 elevation-4 pt-0">
        <main-app-bar title="Master Order Templates">
            <template v-slot:buttons>
                <v-btn color="primary" :loading="isTemplateLoading" @click="submitForm" :disabled="!isOrderTemplateCreated || isTemplateLoading">Save</v-btn>
            </template>
        </main-app-bar>
        <v-flex class="d-block grey lighten-4">
            <div class="pa-3">
                <v-btn-toggle
                    v-model="orderType"
                    color="primary"
                    tile
                    group
                    mandatory
                >
                    <v-btn class="ma-0 mr-3 px-5 py-4" value="Stock">
                        Stock
                    </v-btn>
                    <v-btn class="ma-0 mr-3 px-5 py-4" value="Stationary">
                        Stationary
                    </v-btn>
                </v-btn-toggle>
            </div>
        </v-flex>
        <v-form ref="mainForm">
            <v-alert v-show="error" type="error" style="border-radius: 0" class="pa-3">
                {{error}}
            </v-alert>
            <v-simple-table class="order-template-table" v-if="orderProducts.length && !isTemplateLoading">
                <template v-slot:default>
                    <thead>
                    <tr>
                        <th class="text-center" style="width: 100px;">List Number</th>
                        <th class="text-center" style="width: 100px;">List index</th>
                        <th class="text-center">Product</th>
                        <th class="text-center">Supplier</th>
                        <th class="text-center">Code</th>
                        <th class="text-center">Price, &#163; (inc. VAT)</th>
                        <th class="text-left" style="width: 50px;"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="product in orderProducts" :key="product.id">
                        <td class="py-2">
                            <v-text-field v-model="product.groupNumber" :rules="valueRequiredRule" outlined background-color="grey lighten-3" dense></v-text-field>
                        </td>
                        <td class="py-2">
                            <v-text-field :rules="textOnlyRules" class="text-uppercase" v-model="product.groupLetter" outlined background-color="grey lighten-3" dense ref="listIndex"></v-text-field>
                        </td>
                        <td class="py-2">
                            <v-text-field v-model="product.title" outlined background-color="grey lighten-3" dense></v-text-field>
                        </td>
                        <td>
                            <v-text-field v-model="product.supplier" outlined background-color="grey lighten-3" dense></v-text-field>
                        </td>
                        <td>
                            <v-text-field v-model="product.code" outlined background-color="grey lighten-3" dense></v-text-field>
                        </td>
                        <td>
                            <v-text-field type="number" v-model="product.price" outlined background-color="grey lighten-3" dense></v-text-field>
                        </td>
                        <td class="py-2 text-left">
                            <v-btn icon fab small @click="removeProduct(product.id)">
                                <v-icon>$delete</v-icon>
                            </v-btn>
                        </td>
                    </tr>
                    </tbody>
                </template>
            </v-simple-table>
            <div v-if="!orderProducts.length && !isTemplateLoading" class="text-center pt-5">
                <h3>No added products!</h3>
            </div>
            <div v-if="!isTemplateLoading" class="ma-4 text-center">
                <v-btn color="primary" @click="addNewProduct">
                    <v-icon small>$plus</v-icon>
                    Add
                </v-btn>
            </div>
        </v-form>
        <v-skeleton-loader v-if="isTemplateLoading" type="table"></v-skeleton-loader>
    </v-container>
</template>

<script>
  import MainAppBar from '@/components/MainAppBar'
  import { mapActions, mapGetters } from 'vuex'
  import { stockSortingBySublists } from '../../common/functions'

  export default {
    components: {
      MainAppBar
    },
    data: () => ({
      isTemplateLoading: false,
      orderProducts: [],
      monthlyBudget: null,
      orderTemplateCreating: false,
      pageLoading: true,
      error: '',
      valueRequiredRule: [
        v => !!v || 'Please fill all required fields'
      ],
      textOnlyRules: [
        v => !!v || 'Please set sublist index as letter',
        v => !Number(v) || 'Please set sublist index as letter',
        v => /^[A-Za-z]+$/.test(v) || 'Please set sublist index as letter'
      ],
      orderType: 'Stock'
    }),
    computed: {
      ...mapGetters(['getUserActiveGroup']),
      isOrderTemplateCreated () {
          const emptyProducts = this.orderProducts.filter(item => !item.title || !item.supplier || !item.code || !item.price)
          if (!emptyProducts.length) {
            return true
          } else {
            return false
          }
      }
    },
    watch: {
      userClinicId () {
        this.getOrderTemplate()
      },
      getUserActiveGroup () {
        this.getOrderTemplate()
      },
      orderType () {
        this.getOrderTemplate()
      }
    },
    methods: {
      ...mapActions(['fetchOrderTemplate', 'updateOrderTemplate']),
      async submitForm () {
        this.error = ''
        if (!this.$refs.mainForm.validate()) {
          this.error = 'Wrong list values! Please set List Number as number and List Index as letter'
          return
        }
        if (this.$refs.listIndex[0].value.length > 1) {
          this.error = 'Value of List index must be consist of 1 symbol'
          return
        }
        if (this.isOrderTemplateCreated) {
          this.isTemplateLoading = true
          await this.updateOrderTemplate(this.createData())
          this.isTemplateLoading = false
          this.getOrderTemplate()
        } else {
          this.error = 'Please fill all fields below!'
        }
      },
      createData () {
        return {
          orderType: this.orderType,
          isMaster: true,
          groupId: this.getUserActiveGroup,
          products: this.orderProducts.map(item => {
            return {
              ...item,
              id: item.isNew ? null : item.id,
              price: Number(item.price),
              groupNumber: Number(item.groupNumber)
            }
          })
        }
      },
      async getOrderTemplate () {
        this.monthlyBudget = null
        this.orderProducts = []
        this.isTemplateLoading = true
        const params = {
          orderType: this.orderType,
          groupId: this.getUserActiveGroup
        }
        const result = await this.fetchOrderTemplate(params) || {}
        this.monthlyBudget = result.monthlyBudget || ''
        this.orderProducts = result.products || []
        if (this.orderProducts.length) {
          this.sortProductsByList(this.orderProducts)
        }
        this.isTemplateLoading = false
        this.pageLoading = false
      },
      sortProductsByList (products) {
        products.sort(stockSortingBySublists)
      },
      addNewProduct () {
        const product = {
          id: this.$uuid.v4(),
          title: '',
          supplier: '',
          code: '',
          price: null,
          isNew: true,
          groupNumber: 1,
          groupLetter: 'A'
        }
        this.orderProducts.push(product)
      },
      removeProduct (productId) {
        this.orderProducts = this.orderProducts.filter(({ id }) => id !== productId)
      }
    },
    async mounted () {
      this.getOrderTemplate()
    }
  }
</script>

<style lang="scss">
    .order-template-table {
        tbody {
            td {
                max-width: 100px;
            }
        }
    }
    .text-uppercase input {
        text-transform: uppercase;
    }
</style>
